import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
// @mui
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';

import { fDateYMD } from '../../../utils/formatTime';
import { getAuditBranches } from '../../branches/store/branchActions';
import { getAuditCategories } from '../../categories/store/categoryActions';

const storedUser = JSON.parse(localStorage.getItem('profile'));

const branchRoles = ['MANAGER', 'BRANCH_MANAGER'];

const isManager = storedUser?.role && branchRoles.includes(storedUser.role);

AssetFilter.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onApplyFilter: PropTypes.func,
  onClearFilter: PropTypes.func,
};

const ASSET_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'in_store', label: 'In Store' },
  { value: 'retired', label: 'Retired' },
  { value: 'disposed', label: 'Disposed' },
  // { value: 'UNDER_MAINTENANCE', label: 'Under Maintenance' },
];

export default function AssetFilter({ open, handleClose, onApplyFilter, onClearFilter }) {
  const dispatch = useDispatch();
  //   const { serviceProviders } = useSelector((state) => state.userReducer);
  const { auditBranches } = useSelector((state) => state.branchReducer);
  const { auditCategories } = useSelector((state) => state.categoryReducer);

  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const AssetFilterSchema = Yup.object().shape({});

  const defaultValues = {
    locationId: '',
    categoryId: '',
    assetStatus: '',
    startDate: '',
    endDate: '',
    barcodeNumber: '',
  };

  const handleStartDateChange = (newValue) => {
    setValue('startDate', fDateYMD(newValue));
    setSelectedStartDate(newValue);
  };
  const handleEndDateChange = (newValue) => {
    setValue('endDate', fDateYMD(newValue));
    setSelectedEndDate(newValue);
  };

  const methods = useForm({
    resolver: yupResolver(AssetFilterSchema),
    defaultValues,
    mode: 'onChange',
  });

  const {
    reset,
    watch,
    // control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  // Check if any filters are applied
  const hasFilters =
    values.locationId ||
    values.categoryId ||
    values.assetStatus ||
    values.barcodeNumber?.trim() ||
    values.startDate ||
    values.endDate;

  // useEffect(() => {
  //   dispatch(getServiceProviders({}));
  // }, [dispatch]);

  const onSubmit = async (data) => {
    const payload = {
      locationId: data.locationId || '',
      barcodeNumber: data.barcodeNumber?.trim() || '',
      categoryId: data.categoryId || '',
      assetStatus: data.assetStatus || '',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
    };
    onApplyFilter(payload);
    handleClose();
  };

  const handleClearFilters = () => {
    reset(defaultValues);
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    onClearFilter();
    handleClose();
  };
  useEffect(() => {
    dispatch(getAuditBranches({}));
    dispatch(getAuditCategories({}));
  }, [dispatch]);
  return (
    <Dialog
      maxWidth="md"
      open={open}
      fullWidth
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon color="primary" />
          <Typography variant="h6" component="span">
            Filter Assets
          </Typography>
          {hasFilters && (
            <Typography variant="body2" color="primary" sx={{ ml: 1 }}>
              (
              {Object.values(values).filter((v) => v && v.toString().trim()).length +
                (selectedStartDate ? 1 : 0) +
                (selectedEndDate ? 1 : 0)}{' '}
              active)
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Use the fields below to filter assets. Leave fields empty to include all options.
            </Typography>

            <RHFSelect
              fullWidth
              name="locationId"
              label="Location"
              placeholder="All Locations"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              <MenuItem value="">
                <em>All Locations</em>
              </MenuItem>
              {auditBranches?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFSelect
              fullWidth
              name="categoryId"
              label="Category"
              placeholder="All Categories"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              <MenuItem value="">
                <em>All Categories</em>
              </MenuItem>
              {auditCategories?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFSelect
              fullWidth
              name="assetStatus"
              label="Asset Status"
              placeholder="All Statuses"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              {ASSET_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFTextField
              name="barcodeNumber"
              label="Barcode Number"
              placeholder="Enter barcode number..."
              helperText="Search by exact barcode number"
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              <DatePicker
                label="Start Date"
                value={selectedStartDate}
                format="yyyy-MM-dd"
                onChange={handleStartDateChange}
                slotProps={{
                  textField: {
                    helperText: 'Filter assets from this date',
                    placeholder: 'Select start date',
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={selectedEndDate}
                format="yyyy-MM-dd"
                onChange={handleEndDateChange}
                slotProps={{
                  textField: {
                    helperText: 'Filter assets until this date',
                    placeholder: 'Select end date',
                  },
                }}
              />
            </Box>

            {hasFilters && (
              <Box sx={{ p: 2, backgroundColor: 'primary.lighter', borderRadius: 1 }}>
                <Typography variant="body2" color="primary.main" fontWeight="medium">
                  Active Filters:
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {values.locationId && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Branch: {auditBranches?.find((b) => b.id === values.locationId)?.name}
                    </Typography>
                  )}
                  {values.categoryId && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Category: {auditCategories?.find((c) => c.id === values.categoryId)?.name}
                    </Typography>
                  )}
                  {values.assetStatus && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Status: {ASSET_STATUS_OPTIONS.find((s) => s.value === values.assetStatus)?.label}
                    </Typography>
                  )}
                  {values.barcodeNumber?.trim() && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      Barcode: {values.barcodeNumber}
                    </Typography>
                  )}
                  {selectedStartDate && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      From: {fDateYMD(selectedStartDate)}
                    </Typography>
                  )}
                  {selectedEndDate && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1,
                        py: 0.5,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: 1,
                      }}
                    >
                      To: {fDateYMD(selectedEndDate)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>

          {hasFilters && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={isSubmitting}
            >
              Clear All
            </Button>
          )}

          <LoadingButton
            type="submit"
            size="small"
            variant="contained"
            loading={isSubmitting}
            startIcon={<FilterListIcon />}
            loadingPosition="start"
          >
            Apply Filters
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
